import { Component, Input, OnChanges, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { Platform } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { IAsset } from '@interfaces/asset';
import { BannerNames, BannerOrigin } from '@interfaces/banners';
import { IOperations } from '@interfaces/operations';
import { IPortfolio } from '@interfaces/portfolios';
import { IUser } from '@interfaces/users';
import { AssetsService } from '@services/assets/assets.service';
import { BannersService } from '@services/banners/banners.service';
import { FirebaseService } from '@services/firebase/firebase.service';
import { NavigationService } from '@services/navigation/navigation.service';
import { PortfolioService } from '@services/portfolio/portfolio.service';
import { UserRestrictionsService } from '@services/user-restrictions/user-restrictions.service';

@Component({
  selector: 'app-filter-movements',
  templateUrl: './filter-movements.page.html',
  styleUrls: ['./filter-movements.page.scss'],
})
export class FilterMovementsPage implements OnChanges, OnDestroy {
  @Input() inMiddleColumn: boolean;
  @Input() selected: string;
  @Input() userInfo: IUser;
  backButtonText: string;
  parentId: string;
  parentType: string;
  uid: string;
  isUSD: boolean;
  assetId?: string;
  isWEB: boolean;
  backUrl: string;
  filterBy: string;
  asset: IAsset;
  portfolio: IPortfolio;
  showWithdrawalErrorBanner: boolean;
  private routeSub: Subscription;
  private shouldGoBackToBuyingPower: string;

  constructor(
    public route: ActivatedRoute,
    private platform: Platform,
    private assetsService: AssetsService,
    private portfolioService: PortfolioService,
    private firebaseService: FirebaseService,
    private navigationService: NavigationService,
    private bannersService: BannersService,
    private router: Router,
    private userRestrictionsService: UserRestrictionsService,
  ) {
    this.shouldGoBackToBuyingPower = this.route.snapshot.queryParamMap.get('shouldGoBackToBuyingPower');
    this.backButtonText = this.platform.is('ios') ? 'Atrás' : '';
    this.parentId = this.route.snapshot.queryParamMap.get('parentId');
    this.parentType = this.route.snapshot.queryParamMap.get('parentType') || 'users';
    this.assetId = this.route.snapshot.queryParamMap.get('assetId');
    this.filterBy = this.route.snapshot.queryParamMap.get('filterBy');
    this.uid = this.userInfo ? this.userInfo.uid : this.route.snapshot.queryParamMap.get('uid');
    this.isUSD = !!this.route.snapshot.queryParamMap.get('isUSD');
    this.isWEB = !this.platform.is('cordova');

    this.backUrl = this.assetId ? `/asset-details/${this.assetId}` : `/portfolio/${this.parentId}`;

    if (this.shouldGoBackToBuyingPower) {
      this.backUrl = '/buying-power';
    }

    this.showWithdrawalErrorBanner = this.bannersService.isBannerVisible(BannerNames.withdrawalError, BannerOrigin.movements);
    if (this.assetId) {
      this.getAssetInfo();
    }
    if (this.parentType === 'portfolios') {
      this.setPortfolio();
    }

    this.routeSub = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
    ).subscribe(() => {
      this.bannersService.updateUserWithdrawalErrorNotificationDoc(BannerOrigin.movements, this.uid);
      this.showWithdrawalErrorBanner = this.bannersService.isBannerVisible(BannerNames.withdrawalError, BannerOrigin.movements);
    });
  }

  async ngOnChanges(changes) {
    if (changes.userInfo) {
      if (this.parentType === 'users') {
        this.parentId = this.userInfo.uid;
        this.uid = this.userInfo.uid;
        this.showWithdrawalErrorBanner = await this.bannersService.shouldShowWithdrawalErrorBanner(BannerOrigin.movements, this.uid);
      }
    }
  }

  ngOnDestroy() {
    if (this.routeSub) {
      this.routeSub.unsubscribe();
    }
  }

  getAssetInfo() {
    this.assetsService.getAsset(this.assetId).then(asset => {
      this.asset = asset;
    });
  }

  hasMiddleColumn() {
    return window.innerWidth > 800;
  }

  hasSideMenu() {
    return window.innerWidth >= 570;
  }

  async goToDeposit() {
    const canBuyPortfolio = await this.userRestrictionsService.canOperate(this.userInfo.restrictedReason, IOperations.buyPortfolio);
    const canBuyStocks = await this.userRestrictionsService.canOperate(this.userInfo.restrictedReason, IOperations.buyStocks);
    const canOperate = canBuyPortfolio || canBuyStocks;

    if (!canOperate) { return this.userRestrictionsService.showRestrictedUserToast(); }

    const url = this.isWEB ? '/deposit-info' : '/deposit-amount';
    this.firebaseService.logEvent('go_to_deposits_from_filter_movements_tab', {});
    this.navigationService.goTo(url);
  }

  async goToWithdrawal() {
    const canSellPortfolio = await this.userRestrictionsService.canOperate(this.userInfo.restrictedReason, IOperations.sellPortfolio);
    const canSellStocks = await this.userRestrictionsService.canOperate(this.userInfo.restrictedReason, IOperations.sellStocks);
    const canOperate = canSellPortfolio || canSellStocks;

    if (!canOperate) { return this.userRestrictionsService.showRestrictedUserToast(); }

    this.firebaseService.logEvent('go_to_withdrawal_filter_movements_tab', {});
    this.navigationService.goTo('/withdrawal-amount');
  }

  async goToSendStocks() {
    const canOperate = await this.userRestrictionsService.canOperate(this.userInfo.restrictedReason, IOperations.transferStocks);

    if (!canOperate) { return this.userRestrictionsService.showRestrictedUserToast(); }

    this.firebaseService.logEvent('go_to_send_stocks_filter_movements_tab', {});
    this.navigationService.goTo('/send-stocks-info');
  }

  goToContributionStrategy() {
    this.firebaseService.logEvent('contribution_strategy_filter_mov_tab', {});
    this.navigationService.goTo('/contribution-strategy');
  }

  async setPortfolio() {
    this.portfolio = await this.portfolioService.getPortfolio(this.parentId);

    if (this.portfolio && this.portfolio.isDrivewealth && !this.assetId && !this.shouldGoBackToBuyingPower) {
      this.backUrl = '/stocks-summary';
    }
  }

  goToUpdateBankInfo() {
    this.firebaseService.logEvent('go_to_update_bank_info_from_movements', {});
    this.navigationService.goTo('/bank-information');
  }
}
